import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getPublicProgram, PublicProgramDetail } from "../../lib/publicApi";
import StarField from "../../components/StarField";

export default function ProgramDetail() {
  const { id } = useParams();
  const [p, setP] = useState<PublicProgramDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState<number>(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getPublicProgram(id)
      .then(setP)
      .catch(() => setP(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-obloha-indigo-950 text-slate-400">
        Načítám…
      </div>
    );
  }

  if (!p) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center bg-obloha-indigo-950 text-center">
        <div className="text-4xl">🛸</div>
        <h1 className="mt-4 text-2xl font-bold">Program nenalezen</h1>
        <Link to="/programy" className="mt-4 text-obloha-gold-400 hover:underline">
          ← Zpět na programy
        </Link>
      </div>
    );
  }

  const gallery = [
    ...(p.coverImageUrl ? [{ url: p.coverImageUrl, alt: p.name }] : []),
    ...p.images.map((img) => ({ url: img.url, alt: img.alt ?? p.name })),
  ];
  const mainImage = gallery[activeImage] ?? null;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-obloha-indigo-950 to-obloha-indigo-900 pt-12 pb-8">
        <StarField density={0.00005} />
        <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 translate-x-1/3 rounded-full bg-obloha-gold-500/20 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-6">
          <Link
            to="/programy"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
          >
            ← Všechny programy
          </Link>
        </div>
      </section>

      {/* Detail */}
      <section className="bg-obloha-indigo-950 py-12">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 lg:grid-cols-5">
            {/* Galerie */}
            <div className="lg:col-span-3">
              {mainImage ? (
                <div className="aspect-video overflow-hidden rounded-3xl border border-white/10 bg-obloha-indigo-800 shadow-2xl shadow-obloha-indigo-500/20">
                  <img
                    src={mainImage.url}
                    alt={mainImage.alt ?? p.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex aspect-video items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-obloha-indigo-700 to-purple-800 text-9xl">
                  ✨
                </div>
              )}

              {gallery.length > 1 && (
                <div className="mt-4 grid grid-cols-5 gap-2">
                  {gallery.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImage(i)}
                      className={`aspect-square overflow-hidden rounded-lg border-2 transition ${
                        activeImage === i
                          ? "border-obloha-gold-400"
                          : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <img src={img.url} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="lg:col-span-2">
              <h1 className="text-4xl font-bold md:text-5xl">
                <span className="bg-gradient-to-b from-white to-obloha-indigo-200 bg-clip-text text-transparent">
                  {p.name}
                </span>
              </h1>

              {p.description && (
                <div className="mt-6 whitespace-pre-wrap leading-relaxed text-slate-300">
                  {p.description}
                </div>
              )}

              <div className="mt-8 space-y-3">
                <InfoRow icon="⏱" label="Délka programu">
                  {p.durationMinutes} minut
                </InfoRow>
                <InfoRow icon="👥" label="Kapacita">
                  až {p.maxChildrenPerSlot} dětí najednou
                </InfoRow>
                {p.ageGroupFrom != null && p.ageGroupTo != null && (
                  <InfoRow icon="👶" label="Věková skupina">
                    {p.ageGroupFrom}–{p.ageGroupTo} let
                  </InfoRow>
                )}
                <InfoRow icon="🔧" label="Montáž / demontáž">
                  {p.setupMinutes} / {p.teardownMinutes} min
                </InfoRow>
              </div>

              <div className="mt-8 rounded-2xl border border-obloha-gold-400/30 bg-gradient-to-br from-obloha-gold-500/20 to-transparent p-6">
                <div className="text-xs uppercase text-obloha-gold-300">Cena</div>
                <div className="mt-2 text-3xl font-bold">
                  {p.pricePerChildCzk} Kč{" "}
                  <span className="text-sm font-normal text-slate-300">/ dítě</span>
                </div>
                <div className="mt-1 text-sm text-slate-300">
                  Minimální cena <strong>{p.minPriceCzk.toLocaleString("cs-CZ")} Kč</strong>{" "}
                  + doprava
                </div>
              </div>

              <Link
                to="/rezervace"
                className="mt-6 block w-full rounded-full bg-gradient-to-r from-obloha-gold-500 to-obloha-gold-400 px-8 py-4 text-center text-lg font-bold text-obloha-indigo-950 shadow-xl shadow-obloha-gold-500/30 transition-all hover:scale-[1.02] hover:shadow-obloha-gold-400/50"
              >
                ✨ Rezervovat tento program
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl bg-white/5 p-4">
      <div className="text-2xl">{icon}</div>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
        <div className="mt-0.5 font-medium text-white">{children}</div>
      </div>
    </div>
  );
}
